import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { revalidateLogic, useForm, useFormErrors, useStore } from '@eureca/ui/forms';
import { z } from 'zod';
import {
  JobEditGraduationPage as JobEditGraduationPageComponent,
  isHigherOrTechnical,
} from './JobEditGraduationPage.component';
import type {
  CourseRequirementLevel,
  GraduationType,
  JobEditGraduationValues,
} from './JobEditGraduationPage.component';
import { JobEditGraduationLoadingFallback } from './JobEditGraduationLoadingFallback';
import { JobEditGraduationErrorFallback } from './JobEditGraduationErrorFallback';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '@/features/jobs/job-shared/job-status';
import { JobMutationError, usePatchJob } from '@/features/jobs/job-shared/job-edit-mutation';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';
import {
  coursesByAreaQueryOptions,
  courseTypeIdsForGraduation,
} from '@/features/jobs/job-edit-courses/courses-by-area-query';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';

const GRADUATION_MONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

const GRADUATION_TYPE_VALUES: readonly GraduationType[] = [
  'bacharelado',
  'doutorado',
  'ensino-fundamental',
  'ensino-medio',
  'licenciatura',
  'mestrado',
  'pos-graduacao',
  'tecnico',
  'tecnologo',
];

const COURSE_REQUIREMENT_LEVEL_VALUES: readonly CourseRequirementLevel[] = [
  '',
  'qualquer-curso',
  'quero-especificar',
];

const EMPTY_VALUES: JobEditGraduationValues = {
  graduationTypes: [],
  dateMin: '',
  dateMax: '',
  courseRequirementLevel: '',
};

// Mensagem do erro cross-field min/max (bug Juliana QA): a data máxima de
// formação não pode ser anterior à mínima.
const DATE_RANGE_ERROR = 'A data máxima deve ser igual ou posterior à mínima';

/**
 * `true` quando ambas as datas estão preenchidas, são válidas e a máxima é
 * anterior à mínima. A comparação lexicográfica de string funciona porque
 * ambos os valores estão no formato `YYYY-MM` (zero-padded).
 */
function isDateRangeInvalid(dateMin: string, dateMax: string): boolean {
  return (
    dateMin !== '' &&
    dateMax !== '' &&
    GRADUATION_MONTH_REGEX.test(dateMin) &&
    GRADUATION_MONTH_REGEX.test(dateMax) &&
    dateMax < dateMin
  );
}

const schema = z
  .object({
    graduationTypes: z.array(
      z.enum(GRADUATION_TYPE_VALUES as [GraduationType, ...GraduationType[]])
    ),
    dateMin: z
      .string()
      .refine((v) => v === '' || GRADUATION_MONTH_REGEX.test(v), 'Selecione uma data válida'),
    dateMax: z
      .string()
      .refine((v) => v === '' || GRADUATION_MONTH_REGEX.test(v), 'Selecione uma data válida'),
    courseRequirementLevel: z.enum(
      COURSE_REQUIREMENT_LEVEL_VALUES as [CourseRequirementLevel, ...CourseRequirementLevel[]]
    ),
  })
  .superRefine((val, ctx) => {
    if (isDateRangeInvalid(val.dateMin, val.dateMax)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dateMax'],
        message: DATE_RANGE_ERROR,
      });
    }
    // EU-709 RN-16 — a única regra condicional da entrega. A seção inteira
    // pode ficar em branco (RN-15), mas se o consultor COMEÇA a preencher, as
    // dependências internas passam a valer: informado o tipo de formação, o
    // nível de abertura dos cursos torna-se obrigatório.
    //
    // Isso NÃO depende do tipo da vaga — vale igual nos oito —, por isso a
    // regra vive no formulário da seção e não no resolvedor do Core.
    //
    // EU-768 — só vale para tipo superior/técnico. Ensino médio e Ensino
    // fundamental não têm cursos: o campo "Nível de abertura" nem aparece e o
    // toggle o zera. Exigir o nível ali travava o "Avançar" num erro de campo
    // invisível (sem PATCH, sem toast). Tipo escolar sozinho grava com nível
    // `null` e encerra o fluxo; misturado com superior, a regra volta a valer.
    if (isHigherOrTechnical(val.graduationTypes) && val.courseRequirementLevel === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['courseRequirementLevel'],
        message: COURSE_LEVEL_REQUIRED_ERROR,
      });
    }
  });

/** EU-709 RN-16 — nível de abertura exigido depois de escolher o tipo de formação. */
export const COURSE_LEVEL_REQUIRED_ERROR = 'Selecione o nível de abertura dos cursos';

interface PatchPayload {
  graduationTypes: GraduationType[];
  graduationDateMin: string | null;
  graduationDateMax: string | null;
  courseRequirementLevel: 'qualquer-curso' | 'quero-especificar' | null;
  requiredCourseGroupIds?: number[];
}

function buildPayload(
  values: JobEditGraduationValues,
  requiredCourseGroupIds: number[],
  requiredCourseGroupIdsPruned: boolean
): PatchPayload {
  const level = values.courseRequirementLevel === '' ? null : values.courseRequirementLevel;
  const payload: PatchPayload = {
    graduationTypes: values.graduationTypes,
    graduationDateMin: values.dateMin === '' ? null : values.dateMin,
    graduationDateMax: values.dateMax === '' ? null : values.dateMax,
    courseRequirementLevel: level,
  };
  // Quando o consultor escolhe "qualquer-curso", o sistema MUST limpar
  // qualquer seleção anterior de `requiredCourseGroupIds` no mesmo PATCH
  // (FR-084).
  if (level === 'qualquer-curso') {
    payload.requiredCourseGroupIds = [];
  } else if (requiredCourseGroupIdsPruned) {
    // EU-162 follow-up (Juliana QA item 7) / EU-718: quando o consultor
    // desmarca um tipo de formação, o sistema poda `requiredCourseGroupIds`
    // para manter só os grupos que ainda pertencem aos tipos restantes.
    // Persiste o set pruned no mesmo PATCH para evitar drift entre
    // `graduationTypes` e os grupos salvos.
    payload.requiredCourseGroupIds = requiredCourseGroupIds;
  }
  return payload;
}

type PersistTarget = 'hub' | 'courses';

export default function JobEditGraduationPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/formacao',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const patchJob = usePatchJob(jobId);
  const [hasHydrated, setHasHydrated] = useState(false);
  const lastSavedRef = useRef<JobEditGraduationValues | null>(null);
  // Arma o auto-prune. Fica `true` só quando o consultor DESMARCA um tipo de
  // formação — nunca na hidratação. Sem essa trava, abrir a etapa de uma vaga
  // cujos ids ainda não foram reconciliados (EU-718) podaria tudo sozinho, o
  // que apagaria os requisitos só de visitar a tela.
  const pruneArmedRef = useRef(false);
  // Pruned `requiredCourseGroupIds` set — atualizado quando o consultor
  // desmarca um tipo de formação e algum grupo salvo deixa de pertencer aos
  // tipos restantes. Persistido no próximo submit. `null` = ainda não pruned
  // (usar o set do server).
  const [localRequiredCourseGroupIds, setLocalRequiredCourseGroupIds] = useState<number[] | null>(
    null
  );

  const job = jobQuery.data;
  // EU-569 — vaga Encerrada/Cancelada: seção em modo somente visualização.
  const readOnly = isJobDetailReadOnly(job);

  const showSaveErrorToast = useCallback((err: unknown) => {
    const status = err instanceof JobMutationError ? err.status : 0;
    if (status === 404) {
      toast.error('Vaga não encontrada.');
    } else if (status === 401 || status === 403) {
      toast.error('Você não tem permissão para editar essa vaga.');
    } else {
      toast.error('Não foi possível salvar as alterações. Tente novamente.');
    }
  }, []);

  const persist = useCallback(
    async (values: JobEditGraduationValues, target: PersistTarget) => {
      const navigateNext = () => {
        if (target === 'courses') {
          void navigate({
            to: '/vagas/$jobId/editar/formacao/cursos',
            params: { jobId },
          });
        } else {
          void navigate({
            to: '/vagas/$jobId',
            params: { jobId },
          });
        }
      };
      try {
        const pruned = localRequiredCourseGroupIds !== null;
        const requiredCourseGroupIds =
          localRequiredCourseGroupIds ?? job?.requiredCourseGroupIds ?? [];
        await patchJob.mutateAsync(buildPayload(values, requiredCourseGroupIds, pruned));
        lastSavedRef.current = values;
        toast('Alterações salvas com sucesso', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
        navigateNext();
      } catch (err) {
        showSaveErrorToast(err);
      }
    },
    [
      patchJob,
      navigate,
      jobId,
      showSaveErrorToast,
      localRequiredCourseGroupIds,
      job?.requiredCourseGroupIds,
    ]
  );

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      const target: PersistTarget =
        value.courseRequirementLevel === 'quero-especificar' ? 'courses' : 'hub';
      await persist(value, target);
    },
  });

  useEffect(() => {
    if (hasHydrated || !job) return;
    const j = job as typeof job & {
      graduationDateMin?: string | null;
      graduationDateMax?: string | null;
    };
    const hydrated: JobEditGraduationValues = {
      graduationTypes: [...(job.graduationTypes ?? [])] as GraduationType[],
      dateMin: j.graduationDateMin ?? '',
      dateMax: j.graduationDateMax ?? '',
      courseRequirementLevel: (job.courseRequirementLevel ?? '') as CourseRequirementLevel,
    };
    form.setFieldValue('graduationTypes', hydrated.graduationTypes);
    form.setFieldValue('dateMin', hydrated.dateMin);
    form.setFieldValue('dateMax', hydrated.dateMax);
    form.setFieldValue('courseRequirementLevel', hydrated.courseRequirementLevel);
    lastSavedRef.current = hydrated;
    setHasHydrated(true);
  }, [job, hasHydrated, form]);

  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const formValidationErrors = useFormErrors(form);

  // Erro cross-field derivado direto dos values, para feedback imediato — o
  // `revalidateLogic()` só valida no primeiro submit, então sem isso o erro
  // (e o disable do botão) só apareceria depois de tentar avançar uma vez.
  const dateRangeInvalid = isDateRangeInvalid(values.dateMin, values.dateMax);
  const errors = dateRangeInvalid
    ? { ...formValidationErrors, dateMax: DATE_RANGE_ERROR }
    : formValidationErrors;
  // Botão "Avançar" desabilitado enquanto a faixa de datas for inválida.
  const canSubmit = !dateRangeInvalid;

  // Auto-prune: catálogo de GRUPOS de curso para os tipos ATUALMENTE
  // selecionados. Usado para filtrar `requiredCourseGroupIds` quando um tipo
  // sai da seleção. EU-718 — sempre SEM `query`: o prune tem que comparar
  // contra o catálogo completo do tipo, nunca contra um resultado de busca.
  const courseTypeIds = courseTypeIdsForGraduation(values.graduationTypes);
  const pruneQuery = useQuery({
    ...coursesByAreaQueryOptions(courseTypeIds),
    enabled: courseTypeIds.length > 0,
  });

  // Poda de fato, uma vez por toggle, contra o catálogo dos tipos que
  // SOBRARAM. Espelha o padrão da etapa de Condições de Trabalho, que já
  // reage ao pool de cidades em vez de ler o cache no handler.
  //
  // `isPlaceholderData` é a trava que faz isso valer: `coursesByAreaQueryOptions`
  // usa `keepPreviousData`, então enquanto o catálogo novo não chega o `data`
  // ainda é o dos tipos anteriores — podar ali reintroduziria o bug.
  const prunePlaceholder = pruneQuery.isPlaceholderData;
  const pruneAreas = pruneQuery.data?.areas;
  const noCourseTypes = courseTypeIds.length === 0;
  useEffect(() => {
    if (!pruneArmedRef.current) return;
    // Sem tipo superior/técnico não há catálogo para comparar: a seleção
    // inteira deixa de fazer sentido.
    if (noCourseTypes) {
      pruneArmedRef.current = false;
      setLocalRequiredCourseGroupIds([]);
      return;
    }
    // Catálogo ainda carregando (ou em erro): mantém o estado atual e segue
    // armado — nunca poda no escuro.
    if (prunePlaceholder || !pruneAreas) return;
    const validIds = new Set<number>();
    for (const area of pruneAreas) {
      for (const group of area.courseGroups) validIds.add(group.id);
    }
    pruneArmedRef.current = false;
    setLocalRequiredCourseGroupIds((prev) => {
      const baseIds = prev ?? job?.requiredCourseGroupIds ?? [];
      const pruned = baseIds.filter((id) => validIds.has(id));
      // Nada podado: preserva `null` para o submit não marcar o campo como
      // alterado (FR-084 — só persiste o set quando ele realmente mudou).
      return pruned.length === baseIds.length ? prev : pruned;
    });
  }, [pruneAreas, prunePlaceholder, noCourseTypes, job]);

  function handleToggleGraduationType(value: GraduationType) {
    const current = values.graduationTypes;
    const isSelected = current.includes(value);
    const nextTypes = isSelected ? current.filter((type) => type !== value) : [...current, value];
    form.setFieldValue('graduationTypes', nextTypes);
    // Quando o último higher/technical sai, limpa o nível localmente — o
    // submit subsequente persiste `null` (FR-083).
    if (!isHigherOrTechnical(nextTypes)) {
      form.setFieldValue('courseRequirementLevel', '');
    }
    // Auto-prune (Juliana QA item 7): ao desmarcar um tipo, remover os
    // requiredCourseGroupIds que dependem do tipo removido. Aqui só ARMA — a
    // poda em si roda no efeito abaixo, quando o catálogo dos tipos RESTANTES
    // chega. Podar aqui leria `pruneQuery.data` no mesmo tick do toggle, ou
    // seja, o catálogo de ANTES da mudança: o prune ficava um toggle atrasado
    // e deixava para trás os grupos do tipo recém-removido.
    if (isSelected) {
      pruneArmedRef.current = true;
    }
  }

  function handleDateMinChange(value: string) {
    form.setFieldValue('dateMin', value);
  }

  function handleDateMaxChange(value: string) {
    form.setFieldValue('dateMax', value);
  }

  function handleCourseLevelChange(value: CourseRequirementLevel) {
    form.setFieldValue('courseRequirementLevel', value);
  }

  function handleBack() {
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  // Loading state — primeiro carregamento da vaga.
  if (jobQuery.isLoading) {
    return <JobEditGraduationLoadingFallback />;
  }

  // Erro de carregamento (≠ 404, que já redirecionou via useEffect acima).
  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status !== 404) {
      return <JobEditGraduationErrorFallback onRetry={() => void jobQuery.refetch()} />;
    }
  }

  // Visual-only progress bar.
  const hasAnyValue =
    values.graduationTypes.length > 0 ||
    values.dateMin !== '' ||
    values.dateMax !== '' ||
    values.courseRequirementLevel !== '';
  const progress = hasAnyValue ? 100 : 50;

  return (
    <JobEditGraduationPageComponent
      jobName={job?.name ?? ''}
      companyLogoUrl={companyLogoUrl}
      companyName={companyName}
      values={values as JobEditGraduationValues}
      errors={errors}
      isSubmitting={isSubmitting || patchJob.isPending}
      canSubmit={canSubmit}
      readOnly={readOnly}
      progress={progress}
      onToggleGraduationType={handleToggleGraduationType}
      onDateMinChange={handleDateMinChange}
      onDateMaxChange={handleDateMaxChange}
      onCourseRequirementLevelChange={handleCourseLevelChange}
      onBack={handleBack}
      onSubmit={() => {
        // EU-569 (BLQ-003) — em modo visualização o "Avançar" SÓ NAVEGA
        // (zero PATCH): com "quero-especificar" salvo vai à tela de Cursos,
        // senão retorna ao hub.
        if (readOnly) {
          if (values.courseRequirementLevel === 'quero-especificar') {
            void navigate({
              to: '/vagas/$jobId/editar/formacao/cursos',
              params: { jobId },
            });
          } else {
            void navigate({ to: '/vagas/$jobId', params: { jobId } });
          }
          return;
        }
        // Defesa extra contra Enter no input — o botão já fica disabled.
        if (!canSubmit) return;
        void form.handleSubmit();
      }}
    />
  );
}
